// Config for the immersive "Wellness Worlds". The scenes themselves are now
// fully AI-rendered images (see WorldScene + AvatarContext); this file keeps the
// per-pillar METADATA (label/colour/routes/insight key) and Nikki's fallback
// caption per score-band (shown when there's no live insight yet).
import { moodForScore } from './worldState';

export { moodForScore };

// score → state key (asleep | dormant | stirring | steady | thriving)
export const stateKeyFor = (score) => {
  const b = moodForScore(score).band;
  return b === 'unknown' ? 'asleep' : b;
};

export const STATE_LABEL = {
  asleep: 'Asleep', dormant: 'Dormant', stirring: 'Stirring', steady: 'Steady', thriving: 'Thriving',
};

// Evocative, per-pillar state names — each world speaks its own language
// (Movement "Stagnant→Thriving", Recovery "Depleted→Recharged", etc.).
export const PILLAR_STATE_LABELS = {
  movement:  { dormant: 'Stagnant',    stirring: 'Starting',   steady: 'Active',    thriving: 'Thriving' },
  recovery:  { dormant: 'Depleted',    stirring: 'Restless',   steady: 'Rested',    thriving: 'Recharged' },
  nutrition: { dormant: 'Empty',       stirring: 'Limited',    steady: 'Nourished', thriving: 'Abundant' },
  mental:    { dormant: 'Overwhelmed', stirring: 'Unbalanced', steady: 'Calm',      thriving: 'Aligned' },
};

export const bandLabelFor = (pillarKey, band) => {
  if (!band || band === 'unknown' || band === 'asleep') return 'Awaiting';
  return (PILLAR_STATE_LABELS[pillarKey] || {})[band] || STATE_LABEL[band] || '';
};

export const stateLabelFor = (pillarKey, score) => bandLabelFor(pillarKey, moodForScore(score).band);

// Band → shared scenery image (poor | fair | good | excellent). These are the
// one-time, shared, CHARACTER-FREE world backdrops used on the dashboard tiles.
// Band → shared scenery image (poor | fair | good | excellent). No reading yet
// ('unknown') maps to the NEUTRAL 'fair' — never the gloomy 'poor' — so an
// awaiting/new pillar looks calm-neutral, matching the avatar (pillarStateKey)
// and the backend composite (state_for), not discouraging.
const SCENERY_BY_BAND = { unknown: 'fair', dormant: 'poor', stirring: 'fair', steady: 'good', thriving: 'excellent' };
export const sceneryStateFor = (score) => SCENERY_BY_BAND[moodForScore(score).band] || 'fair';
export const scenerySrc = (pillarKey, score) => `/assets/living/worlds/${pillarKey}_${sceneryStateFor(score)}.webp`;

export const WORLD_CONFIG = {
  movement: {
    key: 'movement', label: 'Movement', short: 'Move', color: '#9BE33B',
    theme: 'Trails · Pathways', detailRoute: '/performance', insightKey: 'movement_insight',
    states: {
      asleep:   { caption: 'Pre-dawn and still — log a movement check-in to wake this trail.' },
      dormant:  { caption: 'Overgrown and overcast — your movement has stalled.' },
      stirring: { caption: 'The clouds are breaking — you’re getting going.' },
      steady:   { caption: 'Clear trail, good light — steady movement.' },
      thriving: { caption: 'Golden hour — you’re thriving. Keep moving.' },
    },
  },
  recovery: {
    key: 'recovery', label: 'Recovery', short: 'Recover', color: '#5FC6E8',
    theme: 'Sleep · Weather · Energy', detailRoute: '/recovery', insightKey: 'recovery_insight',
    states: {
      asleep:   { caption: 'Quiet and dark — no recovery reading yet.' },
      dormant:  { caption: 'Stormy night — you’re running on empty.' },
      stirring: { caption: 'Clouds clearing — recovery is picking up.' },
      steady:   { caption: 'Calm dusk — you’re recovering well.' },
      thriving: { caption: 'Serene sunrise — fully recharged.' },
    },
  },
  nutrition: {
    key: 'nutrition', label: 'Nutrition', short: 'Fuel', color: '#E0B96A',
    theme: 'Gardens · Growth', detailRoute: '/nutrition', insightKey: 'nutrition_insight',
    states: {
      asleep:   { caption: 'A dormant garden — no nutrition reading yet.' },
      dormant:  { caption: 'Wilted and barren — your fuel is low.' },
      stirring: { caption: 'New shoots — healthy habits are forming.' },
      steady:   { caption: 'Green and growing — solid fueling.' },
      thriving: { caption: 'Lush and blooming — your fuel feeds growth.' },
    },
  },
  mental: {
    key: 'mental', label: 'Mental Readiness', short: 'Mind', color: '#A9A7E8',
    theme: 'Atmosphere · Calm', detailRoute: '/mental', insightKey: 'mental_insight',
    states: {
      asleep:   { caption: 'Still and quiet — no mental reading yet.' },
      dormant:  { caption: 'Heavy storm — your mind feels clouded.' },
      stirring: { caption: 'The clouds are thinning — finding some calm.' },
      steady:   { caption: 'Cool and calm — a steady headspace.' },
      thriving: { caption: 'Clear skies — your mind is grounded.' },
    },
  },
};
